import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { t } from 'i18next';
import { ArrowRightSmall } from '@peacecover/icon';
import MyPageMenuList from './MyPageMenuList';
import RoundedCard from './RoundedCard';
import { BettieChatCount } from '../../chat/components/BettieCreditBanner';
import { ReactComponent as PlusProfile } from '../../../icons/plus.profile.svg';
import { SquareBadge } from '@peacecover/component';
import { daysUntil } from '../../../utils/DateUtil';
import type { components } from '../../../types/schema';

type MySubscriptionType = components['schemas']['ApiV1Subscription'];

const bettieProfileMenu = [
  {
    title: 'myPage.myPageMenuItem.bettiePurchaseHistory',
    path: '/bettie/purchase-history'
  },
  {
    title: 'myPage.myPageMenuItem.bettieUsage',
    path: '/bettie/usage'
  }
];

interface BettieProfileCardProps {
  // biome-ignore lint/suspicious/noExplicitAny: any type is used because the type is not defined
  bettieChatRooms: any;
  mySubscription: MySubscriptionType | undefined;
}
const BettieProfileCard = ({ bettieChatRooms, mySubscription }: BettieProfileCardProps) => {
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (bettieChatRooms?.length > 0) {
      navigate('/bettie-chat-list');
    } else {
      navigate('/bettie-setting?isFirst=true');
    }
  };

  const profileBadge = () => {
    if (isEmpty(mySubscription)) {
      return (
        <>
          <BettieProfileText bettieChatRooms={bettieChatRooms} />
          <BettieChatCount
            className="gap-[2px]"
            textClassName="ml-[2px] !text-[11px] !leading-[13px]"
          />
        </>
      );
    }

    const productType = mySubscription.productType === 'standard' ? '스탠다드' : '프리미엄';
    if (mySubscription.canceledAt) {
      const cancelDday = daysUntil(mySubscription.nextBillingDate);
      return (
        <>
          <BettieChatCount
            className="gap-[2px]"
            textClassName="ml-[2px] !text-[11px] !leading-[13px]"
          />
          <div className="flex items-center gap-[4px]">
            <SquareBadge colorVariant="secondary" className="text-text-primary">
              {t('myPage.toBeCancel', {
                product: productType
              })}
            </SquareBadge>
            <SquareBadge className="bg-surface-lv2">
              {t('myPage.expiredDay', { day: cancelDday ?? '-' })}
            </SquareBadge>
          </div>
        </>
      );
    }

    const nextBillingDday = daysUntil(mySubscription.nextBillingDate);
    return (
      <>
        <BettieChatCount
          className="gap-[2px]"
          textClassName="ml-[2px] !text-[11px] !leading-[13px]"
        />
        <div className="flex items-center gap-[4px]">
          <SquareBadge colorVariant="secondary" className="text-text-primary">
            {t('myPage.useSubscription', {
              product: productType
            })}
          </SquareBadge>
          <SquareBadge className="bg-surface-lv2">
            {t('myPage.nextBillingDay', { day: nextBillingDday ?? '-' })}
          </SquareBadge>
        </div>
      </>
    );
  };

  return (
    <RoundedCard>
      <div className="px-[20px] pb-[12px]">
        <div className="flex pt-[20px] pb-[6px] gap-[12px] items-center">
          <BettieProfileImage bettieChatRooms={bettieChatRooms} />
          <button
            type="button"
            className="flex justify-between w-full items-center"
            onClick={handleNavigate}
          >
            <div className="flex flex-col gap-[8px] items-start">{profileBadge()}</div>
            <ArrowRightSmall size={16} className="text-text-main-lv3" />
          </button>
        </div>
        <MyPageMenuList items={bettieProfileMenu} />
      </div>
    </RoundedCard>
  );
};

interface BettieProfileImageProps {
  // biome-ignore lint/suspicious/noExplicitAny: any type is used because the type is not defined
  bettieChatRooms: any;
}

const BettieProfileImage = ({ bettieChatRooms }: BettieProfileImageProps) => {
  const navigate = useNavigate();

  if (bettieChatRooms?.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigate('/bettie-setting?isFirst=true')}
        className="w-[56px] h-[56px] rounded-full overflow-hidden shrink-0"
      >
        <PlusProfile width={56} height={56} />
      </button>
    );
  }

  return (
    <div className="relative w-[56px] h-[56px] min-w-[56px] min-h-[56px] rounded-full overflow-hidden shrink-0">
      <img
        src={bettieChatRooms?.[0]?.bettieProfile?.thumbnailUrl}
        alt="bettie profile"
        className="w-[56px] h-[56px] rounded-full object-cover"
      />
      <img
        src="/assets/icons/profile_bubble.png"
        alt="profile bubble"
        className="absolute inset-0 w-full h-full object-fill z-10 pointer-events-none"
      />
    </div>
  );
};

interface BettieProfileTextProps {
  // biome-ignore lint/suspicious/noExplicitAny: any type is used because the type is not defined
  bettieChatRooms: any;
}

const BettieProfileText = ({ bettieChatRooms }: BettieProfileTextProps) => {
  if (bettieChatRooms && bettieChatRooms.length <= 1) {
    return (
      <p className="text-[13px] font-medium text-text-primary leading-[16px]">
        {t('myPage.bettieWaiting')}
      </p>
    );
  }
  return (
    <p className="text-[13px] font-medium text-text-main-lv2 leading-[16px]">
      {t('myPage.chatStatusWithCount', {
        nickname: bettieChatRooms?.[0]?.bettieProfile?.nickname,
        count: bettieChatRooms?.length && bettieChatRooms.length - 1
      })}
    </p>
  );
};

export default BettieProfileCard;
